/**
 * Sequence Processor — v1.5.5
 *
 * Client-side background loop that fires due sequence emails from whichever
 * browser is currently open. Runs every 60 seconds while CHARLIE is open,
 * only during business hours (weekdays 7am-6pm CT).
 *
 * Send identity:
 *   sequence.persona (partner name) → partners row → partner mailbox token.
 *   Sends are ALWAYS from the sequence's bound partner, never the active persona.
 *
 * Idempotency:
 *   sequence_sends(contact_sequence_id, step_number) UNIQUE — a step can only
 *   be logged once per enrollment. Duplicate fires are rejected at the DB.
 *
 * Safety gates before each send:
 *   1. sequence.active = true
 *   2. contact.do_not_contact = false
 *   3. contact.contact_status not in blocked set (retired/deceased/between_roles/departed)
 *   4. contact.email present
 *   5. sequence_sends row for (enrollment, step) does not exist
 *   6. Business hours check
 */

import { supabase } from './supabase';
import { getMsToken, sendGraphEmail, sendGmailEmail } from './msal';
import { getGoogleTokenForPartner } from './supabaseAuth';
import { renderTemplate } from './templateRenderer';
import { getOutboundSignature, wrapWithSignature, getClosingBlock } from './userSignatures';

const POLL_INTERVAL_MS = 60 * 1000; // 60s
// v1.7.25 — the ONE primary Microsoft connection all shared-mailbox personas send through.
const WELL_REPD_PARTNER_ID = '6ad8ac03-049f-462f-83fc-8b2bbc7c5bdd';
const BUSINESS_HOURS_START = 7;      // 7am CT
const BUSINESS_HOURS_END = 18;       // 6pm CT (18:00)
const BUSINESS_DAYS = [1, 2, 3, 4, 5]; // Mon-Fri

const STATUSES_BLOCKING_OUTREACH = new Set([
  'retired', 'deceased', 'between_roles', 'departed', 'industry_inactive',
]);

let running = false;
let intervalId = null;
let lastRunAt = null;

/** Check if now is inside the business-hours send window (America/Chicago). */
function inBusinessHours(now = new Date()) {
  // Convert to CT using Intl API rather than raw offset math (handles DST).
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    weekday: 'short',
    hour: 'numeric',
    hour12: false,
  }).formatToParts(now);
  const weekdayShort = parts.find((p) => p.type === 'weekday')?.value;
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value, 10);
  const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dow = dayMap[weekdayShort];
  if (!BUSINESS_DAYS.includes(dow)) return false;
  return hour >= BUSINESS_HOURS_START && hour < BUSINESS_HOURS_END;
}

/**
 * Main tick — pulls due enrollments, sends step, advances or completes.
 * Returns { attempted, sent, skipped, errors }.
 */
const AUTOMATIC_SEQUENCE_SENDING_ENABLED = false;
export async function processDueSequences({ currentUserId } = {}) {
  // Shane 2026-09-29: nothing sends automatically. Sequence emails are confirmed by a person in the composer.
  if (AUTOMATIC_SEQUENCE_SENDING_ENABLED !== true) return { attempted: 0, sent: 0, skipped: 0, errors: 0, disabled: 'human_approval_required' };
  if (!inBusinessHours()) {
    return { attempted: 0, sent: 0, skipped: 0, errors: 0, reason: 'outside_business_hours' };
  }

  const nowIso = new Date().toISOString();

  // 1. Pull due enrollments
  const { data: due, error: dueErr } = await supabase
    .from('contact_sequences')
    .select(`
      id, contact_id, sequence_id, current_step, status, next_send_at,
      contact:contacts(id, email, first_name, last_name, full_name, job_title, company_id, primary_discipline, discipline, region_or_state, contact_status, do_not_contact, unsubscribe_token),
      sequence:sequences(id, name, persona, active,
        sequence_steps(id, step_number, delay_days, subject, body, template_id, cc_emails, bcc_emails, attachments)
      )
    `)
    .eq('status', 'active')
    .lte('next_send_at', nowIso)
    .limit(25);

  if (dueErr) {
    console.warn('[sequenceProcessor] fetch failed:', dueErr.message);
    return { attempted: 0, sent: 0, skipped: 0, errors: 1 };
  }

  if (!due || due.length === 0) {
    return { attempted: 0, sent: 0, skipped: 0, errors: 0 };
  }

  let sent = 0;
  let skipped = 0;
  let errors = 0;

  for (const row of due) {
    try {
      // 2. Gate: sequence still active
      if (!row.sequence?.active) {
        await supabase.from('contact_sequences')
          .update({ status: 'paused', paused_at: nowIso })
          .eq('id', row.id);
        skipped++;
        continue;
      }

      // 3. Gate: contact still contactable
      const c = row.contact;
      if (!c || !c.email || c.do_not_contact || STATUSES_BLOCKING_OUTREACH.has(c.contact_status)) {
        await supabase.from('contact_sequences')
          .update({ status: 'stopped_dnc', completed_at: nowIso })
          .eq('id', row.id);
        skipped++;
        continue;
      }

      // 4. Resolve current step.
      // v1.7.26 — fetch steps EXPLICITLY by sequence_id rather than trusting the nested join.
      // The nested join (sequences(... sequence_steps(...))) was intermittently returning zero
      // steps at runtime, which made the processor mark healthy enrollments 'completed' WITHOUT
      // ever sending (that's why the first email never arrived despite 6 steps existing). An
      // explicit query is reliable.
      let steps = (row.sequence.sequence_steps || []).slice();
      if (steps.length === 0) {
        const { data: freshSteps } = await supabase
          .from('sequence_steps')
          .select('id, step_number, delay_days, subject, body, template_id, cc_emails, bcc_emails, attachments')
          .eq('sequence_id', row.sequence_id);
        steps = freshSteps || [];
      }
      steps.sort((a, b) => a.step_number - b.step_number);
      // v1.7.27a — DIAGNOSTIC: log the resolve so we can see exactly what the processor sees.
      try {
        await supabase.from('processor_events').insert({
          event_type: 'debug_resolve',
          payload: {
            contact_sequence_id: row.id,
            sequence_id: row.sequence_id,
            current_step: row.current_step,
            steps_from_join: (row.sequence.sequence_steps || []).length,
            steps_resolved: steps.length,
            persona: row.sequence?.persona || null,
            step_numbers: steps.map((x) => x.step_number),
          },
        });
      } catch (_e) { /* non-fatal */ }
      if (steps.length === 0) {
        await supabase.from('contact_sequences')
          .update({ status: 'completed', completed_at: nowIso })
          .eq('id', row.id);
        skipped++;
        continue;
      }
      const stepIdx = row.current_step || 0;
      if (stepIdx >= steps.length) {
        await supabase.from('contact_sequences')
          .update({ status: 'completed', completed_at: nowIso })
          .eq('id', row.id);
        skipped++;
        continue;
      }
      const step = steps[stepIdx];

      // 5. Resolve partner from persona (sequence-bound sender rule)
      const personaName = row.sequence.persona;
      if (!personaName) {
        console.warn(`[sequenceProcessor] Sequence ${row.sequence.id} has no persona; skipping`);
        skipped++;
        continue;
      }
      // v1.7.25 — ROOT-CAUSE FIX: sequences store `persona` as the mailbox EMAIL
      // (e.g. foxset@wellrepd.com), but this used to match on partner NAME ("FoxSet") — so the
      // lookup ALWAYS found nothing and EVERY sequence send was silently skipped. Match by email
      // OR name so it resolves regardless of which form is stored.
      const cols = 'id, name, email, email_provider, one_liner, intro_paragraph, pain_question, discipline_pitch, latest_release_or_result, rate_limit_per_hour, rate_limit_per_day';
      let { data: partner } = await supabase
        .from('partners').select(cols)
        .eq('email', personaName).eq('active', true).maybeSingle();
      if (!partner) {
        const byName = await supabase
          .from('partners').select(cols)
          .eq('name', personaName).eq('active', true).maybeSingle();
        partner = byName.data;
      }
      if (!partner || !partner.email) {
        console.warn(`[sequenceProcessor] Partner for persona "${personaName}" not found or has no mailbox`);
        try { await supabase.from('processor_events').insert({ event_type: 'debug_no_partner', payload: { contact_sequence_id: row.id, persona: personaName } }); } catch (_e) {}
        skipped++;
        continue;
      }

      // v1.6.0: Rate-limit gate per partner mailbox
      const hourLimit = partner.rate_limit_per_hour ?? 30;
      const dayLimit  = partner.rate_limit_per_day  ?? 200;
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const dayAgo  = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const [{ count: hourCount }, { count: dayCount }] = await Promise.all([
        supabase.from('sequence_sends')
          .select('id', { count: 'exact', head: true })
          .eq('sender_partner_id', partner.id)
          .gte('sent_at', hourAgo),
        supabase.from('sequence_sends')
          .select('id', { count: 'exact', head: true })
          .eq('sender_partner_id', partner.id)
          .gte('sent_at', dayAgo),
      ]);
      if ((hourCount || 0) >= hourLimit || (dayCount || 0) >= dayLimit) {
        // Requeue this enrollment 1h out and skip. Processor will pick it up again.
        const requeue = new Date(Date.now() + 60 * 60 * 1000).toISOString();
        await supabase.from('contact_sequences')
          .update({ next_send_at: requeue })
          .eq('id', row.id);
        // v1.6.1: log rate-limit hits so the Health widget can show throughput drops
        try {
          await supabase.from('processor_events').insert({
            event_type: 'rate_limit_hit',
            partner_id: partner.id,
            payload: {
              partner_name: partner.name,
              hour_count: hourCount || 0,
              hour_limit: hourLimit,
              day_count: dayCount || 0,
              day_limit: dayLimit,
              contact_sequence_id: row.id,
              which: (hourCount || 0) >= hourLimit ? 'hour' : 'day',
            },
          });
        } catch (_e) { /* non-fatal */ }
        console.info(`[sequenceProcessor] rate-limited ${partner.name} (${hourCount}/${hourLimit}h, ${dayCount}/${dayLimit}d) — requeued 1h`);
        skipped++;
        continue;
      }

      // 6. Fetch mailbox token (Microsoft or Google based on partner.email_provider)
      const provider = partner.email_provider || 'microsoft';
      let token = null;
      if (provider === 'microsoft') {
        token = await getMsToken(partner.email, partner.id);
        // v1.7.25 — shared-mailbox personas (Phoenix, FoxSet, etc.) have NO own token; they ride
        // the ONE primary Well Repd connection (partner 6ad8ac03) via Exchange Send-As. If this
        // persona has no token, fall back to the Well Repd primary token. Without this, every
        // shared-persona sequence send was skipped for 'no token'.
        if (!token) {
          token = await getMsToken('shane.deason@wellrepd.com', WELL_REPD_PARTNER_ID);
        }
      } else if (provider === 'google') {
        const rec = await getGoogleTokenForPartner(partner.id);
        token = rec?.access_token || null;
      } else {
        console.warn(`[sequenceProcessor] Partner ${partner.name} uses unknown provider "${provider}"; skipping`);
        skipped++;
        continue;
      }
      if (!token) {
        console.warn(`[sequenceProcessor] No valid ${provider} token for ${partner.email}; skipping`);
        try { await supabase.from('processor_events').insert({ event_type: 'debug_no_token', payload: { contact_sequence_id: row.id, persona: personaName, partner_email: partner.email } }); } catch (_e) {}
        skipped++;
        continue;
      }

      // 8. Render template with contact + partner context
      let valueProp = null;
      const { data: vp } = await supabase
        .from('partner_value_props')
        .select('*')
        .eq('partner_id', partner.id)
        .maybeSingle();
      valueProp = vp;

      const senderName = 'Shane'; // Voice atlas: always sign off Shane, never Shane Deason
      const signature = await getOutboundSignature(partner.id, partner);
      const closingBlock = await getClosingBlock();
      const renderCtx = {
        contact: c,
        partner,
        valueProp,
        task: {},
        sender: { name: senderName, signature: signature || '', closingBlock: closingBlock || '' },
      };
      const renderedSubject = renderTemplate(step.subject || '', renderCtx);
      const renderedBodyRaw = renderTemplate(step.body || '', renderCtx);
      const renderedBodyWithSig = wrapWithSignature(renderedBodyRaw, signature || '');

      // v1.5.13: append unsubscribe footer (CAN-SPAM compliance + Gmail/Outlook deliverability)
      const appBase = process.env.APP_BASE_URL || 'https://charlie.wellrepd.com';
      const unsubUrl = c.unsubscribe_token ? `${appBase}/api/unsubscribe?token=${c.unsubscribe_token}` : null;
      const renderedBody = unsubUrl
        ? `${renderedBodyWithSig}\n\n---\nDon't want these emails? Unsubscribe: ${unsubUrl}`
        : renderedBodyWithSig;
      const listUnsubHeader = unsubUrl ? `<${unsubUrl}>` : null;

      // 9. Idempotency lock — try to reserve the sequence_sends row first
      const { error: lockErr } = await supabase.from('sequence_sends').insert({
        contact_sequence_id: row.id,
        sequence_id: row.sequence.id,
        contact_id: c.id,
        step_number: step.step_number,
        subject: renderedSubject,
        sent_by: currentUserId || null,
        sender_email: partner.email,
        sender_partner_id: partner.id,
      });
      if (lockErr) {
        // Duplicate = another tab already fired this step, skip silently
        if (lockErr.code === '23505') { skipped++; continue; }
        console.warn('[sequenceProcessor] lock insert failed:', lockErr.message);
        errors++;
        continue;
      }

      // 10. Send via Graph or Gmail depending on provider
      let sendResult = null;
      try {
        const sendArgs = {
          token,
          from: partner.email,
          to: c.email,
          cc: (step.cc_emails || []).join(','),
          bcc: (step.bcc_emails || []).join(','),
          subject: renderedSubject,
          body: renderedBody,
          listUnsubscribe: listUnsubHeader, // v1.5.13 RFC 8058 one-click header
        };
        if (provider === 'google') {
          sendResult = await sendGmailEmail(sendArgs);
        } else {
          sendResult = await sendGraphEmail(sendArgs);
        }
      } catch (sendErr) {
        // Roll back the lock so we can retry later
        await supabase.from('sequence_sends')
          .update({ error: sendErr.message })
          .eq('contact_sequence_id', row.id)
          .eq('step_number', step.step_number);
        console.error(`[sequenceProcessor] send failed for ${c.email}:`, sendErr.message);
        errors++;
        continue;
      }

      // v1.5.14: persist thread_id + message_id so replyChecker can detect replies later
      // v1.7.41.72 (Ledger #1290) -- now stores internet_message_id too.
      // graph_message_id alone was useless for bounce detection: it is
      // the DRAFT id and stops resolving once the message is sent.
      // internet_message_id is the RFC 5322 Message-ID that bounces and
      // replies quote in In-Reply-To, so it is the only correlatable
      // handle. 219 of 235 sends were untrackable without it.
      if (sendResult && (sendResult.threadId || sendResult.messageId || sendResult.internetMessageId)) {
        await supabase.from('sequence_sends')
          .update({
            thread_id: sendResult.threadId || null,
            graph_message_id: sendResult.messageId || null,
            internet_message_id: sendResult.internetMessageId || null,
          })
          .eq('contact_sequence_id', row.id)
          .eq('step_number', step.step_number);
      }

      // 11. Advance enrollment
      const nextIdx = stepIdx + 1;
      if (nextIdx >= steps.length) {
        // Sequence complete
        await supabase.from('contact_sequences')
          .update({ current_step: nextIdx, status: 'completed', completed_at: nowIso, next_send_at: null })
          .eq('id', row.id);
      } else {
        const nextStep = steps[nextIdx];
        const nextDelayMs = Math.max(0, Number(nextStep.delay_days || 0)) * 86400000;
        const nextSend = new Date(Date.now() + nextDelayMs).toISOString();
        await supabase.from('contact_sequences')
          .update({ current_step: nextIdx, next_send_at: nextSend })
          .eq('id', row.id);
      }

      sent++;
    } catch (err) {
      console.error('[sequenceProcessor] unexpected error:', err);
      errors++;
    }
  }

  lastRunAt = new Date();
  return { attempted: due.length, sent, skipped, errors };
}

/** Start the background loop. Idempotent — safe to call multiple times. */
export function startSequenceProcessor({ currentUserId, onTick } = {}) {
  if (running) return;
  running = true;

  let tickCount = 0;
  const REPLY_CHECK_EVERY_N_TICKS = 5; // ~5 minutes

  const tick = async () => {
    tickCount++;
    try {
      const result = await processDueSequences({ currentUserId });
      if (onTick && (result.sent || result.errors)) {
        onTick(result);
      }
    } catch (err) {
      console.error('[sequenceProcessor] tick failed:', err);
    }
    // v1.5.14: reply + bounce detection on a slower cadence
    if (tickCount % REPLY_CHECK_EVERY_N_TICKS === 0) {
      try {
        const { checkForRepliesAndBounces } = await import('./replyChecker');
        const rc = await checkForRepliesAndBounces();
        if (onTick && (rc.replies || rc.bounces || rc.errors)) {
          onTick({ replyCheck: rc });
        }
      } catch (err) {
        console.error('[replyChecker] tick failed:', err);
      }
    }
  };

  // Run once immediately, then on interval
  tick();
  intervalId = setInterval(tick, POLL_INTERVAL_MS);
}

/** Stop the background loop. */
export function stopSequenceProcessor() {
  running = false;
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

export function getProcessorStatus() {
  return { running, lastRunAt, inBusinessHours: inBusinessHours() };
}
